import React, { useEffect, useRef, useState } from "react";
import { getFormationMetaEngines, getFormationMetas } from "shared/services/api-assistant.js";
import BoxCard from "shared/components/box/BoxCard.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import PopupFormationInfo from "shared/components/popups/PopupFormationInfo.js";
import "./PageFormationMeta.css";
import "./../bars/MenuTop.css";
import { isUserPremium } from "shared/utils/user.js";
import PopupInformationFormationMeta from "shared/components/popups/PopupInformationFormationMeta.js";
import PopupPremiumInfo from "shared/components/popups/PopupPremiumInfo.js";
import Popup from "reactjs-popup";
import ButtonSelect from "shared/components/buttons/ButtonSelect.js";

const PageFormationMeta = (props) => {
  const [engines, setEngines] = useState(null);
  const [selectedEngine, setSelectedEngine] = useState(null);
  const [selectedType, setSelectedType] = useState("PRO");
  const formationMetaRequestId = useRef(0);
  const [formationMetas, setFormationMetas] = useState(null);
  const [formations, setFormations] = useState(null);
  const [dataMap, setDataMap] = useState(null);
  const [globalDataMap, setGlobalDataMap] = useState(null);
  const [totalMatches, setTotalMatches] = useState(null);
  const [displays, setDisplays] = useState([
    "Victory rate",
    "Victory and draw rate",
    "Point average",
    "Average goals scored",
    "Average goals conceded",
    "Average goal difference",
  ]);
  const [selectedDisplay, setSelectedDisplay] = useState("Victory rate");
  const [selectedDifference, setSelectedDifference] = useState(22);
  const [detailedView, setDetailedView] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" });

  const [minOverall, setMinOverall] = useState(null);
  const [maxOverall, setMaxOverall] = useState(null);
  const [maxDiff, setMaxDiff] = useState(null);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  const sortedFormations = React.useMemo(() => {
    if (!globalDataMap || !formations) return formations || [];
    if (!sortConfig.key) return formations;

    return [...formations].sort((a, b) => {
      const aData = globalDataMap[a];
      const bData = globalDataMap[b];
      if (!aData || !bData) return 0;

      const aTotal = aData.victories + aData.draws + aData.defeats;
      const bTotal = bData.victories + bData.draws + bData.defeats;

      let aRate = 0;
      let bRate = 0;

      if (sortConfig.key === "difference") {
        aRate = aTotal > 0 ? (aData["goalsScored"] - aData["goalsConceded"]) / aTotal : 0;
        bRate = bTotal > 0 ? (bData["goalsScored"] - bData["goalsConceded"]) / bTotal : 0;
      } else {
        aRate = aTotal > 0 ? aData[sortConfig.key] / aTotal : 0;
        bRate = bTotal > 0 ? bData[sortConfig.key] / bTotal : 0;
      }

      if (aRate < bRate) return sortConfig.direction === "asc" ? -1 : 1;
      if (aRate > bRate) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [formations, globalDataMap, sortConfig]);

  const fetchFormationMetaEngines = () => {
    getFormationMetaEngines({
      handleSuccess: (d) => {
        const orderedEngines = d.data.getFormationMetaEngines.sort((a, b) => {
          const parsePart = (v) => {
            if (!v) return { major: 0, minor: 0, patch: null, isBeta: false, betaNum: Infinity };
            const parts = v.split(".");
            const major = +parts[0] || 0;
            const minor = +parts[1] || 0;

            let patch = null;
            let isBeta = false;
            let betaNum = Infinity;

            if (parts[2] !== undefined) {
              const betaSplit = parts[2].split("-beta.");
              patch = +betaSplit[0] || 0;
              if (betaSplit[1] !== undefined) {
                isBeta = true;
                betaNum = +betaSplit[1] || Infinity;
              }
            }

            return { major, minor, patch, isBeta, betaNum };
          };

          const parseVersion = (v) => {
            const [mainPart, subPart] = v.split("/");
            return {
              main: parsePart(mainPart),
              sub: parsePart(subPart),
            };
          };

          const compareVersion = (x, y) => {
            // coarse-only (patch === null) comes first
            if (x.patch === null && y.patch !== null) return -1;
            if (x.patch !== null && y.patch === null) return 1;

            // Both coarse-only or both have patch
            // Now ascending patch order (coarse first)
            const patchCmp = (x.patch || 0) - (y.patch || 0);
            if (patchCmp !== 0) return patchCmp;

            // Beta versions: stable first, beta later
            if (x.isBeta !== y.isBeta) return x.isBeta ? 1 : -1;

            return x.betaNum - y.betaNum;
          };

          const va = parseVersion(a);
          const vb = parseVersion(b);

          // 1. Compare coarse x.x descending
          const coarseCmp = vb.main.major - va.main.major || vb.main.minor - va.main.minor;
          if (coarseCmp !== 0) return coarseCmp;

          // 2. Compare main fine x.x.x (coarse first)
          const mainCmp = compareVersion(va.main, vb.main);
          if (mainCmp !== 0) return mainCmp;

          // 3. Compare sub /xxxxx (coarse first)
          return compareVersion(va.sub, vb.sub);
        });

        setEngines(orderedEngines);
        setSelectedEngine(orderedEngines.find((e) => !e.includes("beta")) ?? null);
      },
      handleError: (e) => console.log(e),
    });
  };

  const fetchFormationMetas = () => {
    const requestId = ++formationMetaRequestId.current;
    getFormationMetas({
      handleSuccess: (d) => {
        if (requestId !== formationMetaRequestId.current) return;
        setFormationMetas(d.data.getFormationMetas);

        const set = new Set();
        d.data.getFormationMetas.forEach((item) => {
          set.add(item.formation1);
          set.add(item.formation2);
        });
        setFormations(Array.from(set).sort());

        const dataMap = {};
        d.data.getFormationMetas.forEach(
          ({ formation1, formation2, victories, draws, defeats, goalsScored, goalsConceded, engine }) => {
            dataMap[`${formation1}||${formation2}`] = {
              formation1,
              formation2,
              victories,
              draws,
              defeats,
              goalsScored,
              goalsConceded,
              engine,
            };
          },
        );
        setDataMap(dataMap);

        const globalDataMap = {};
        d.data.getFormationMetas.forEach(
          ({ formation1, victories, draws, defeats, goalsScored, goalsConceded, engine }) => {
            if (!globalDataMap[formation1]) {
              globalDataMap[formation1] = {
                formation1,
                formation2: null,
                victories: 0,
                draws: 0,
                defeats: 0,
                goalsScored: 0,
                goalsConceded: 0,
                engine: engine,
              };
            }

            globalDataMap[formation1].victories += victories;
            globalDataMap[formation1].draws += draws;
            globalDataMap[formation1].defeats += defeats;
            globalDataMap[formation1].goalsScored += goalsScored;
            globalDataMap[formation1].goalsConceded += goalsConceded;
          },
        );
        setGlobalDataMap(globalDataMap);

        setTotalMatches(
          d.data.getFormationMetas.map((m) => m.victories + m.draws + m.defeats).reduce((sum, val) => sum + val, 0) / 2,
        );
      },
      handleError: (e) => console.log(e),
      params: { engine: selectedEngine, difference: selectedDifference, type: selectedType },
    });
  };

  const shortenFormationName = (n) => {
    return n.replace(/([a-zA-Z])[a-zA-Z]*/g, "$1");
  };

  const getDetailCellValue = (data) => {
    if (!data || !data.victories || !data.draws || !data.defeats || !data.goalsScored || !data.goalsConceded) {
      return "";
    }

    const matches = data.victories + data.draws + data.defeats;

    if (matches === 0) return "";

    switch (selectedDisplay) {
      case "Victory rate":
        return Math.round((data.victories / matches) * 100);

      case "Victory and draw rate":
        return Math.round(((data.victories + data.draws) / matches) * 100);

      case "Point average":
        return Math.round(((data.victories * 3 + data.draws) / matches) * 100) / 100;

      case "Average goal difference":
        return Math.round(((data.goalsScored - data.goalsConceded) / matches) * 100) / 100;

      case "Average goals scored":
        return Math.round((data.goalsScored / matches) * 100) / 100;

      case "Average goals conceded":
        return Math.round((data.goalsConceded / matches) * 100) / 100;

      default:
        return "";
    }
  };

  const countMatches = (data) => {
    return data && data.victories && data.draws && data.defeats ? data.victories + data.draws + data.defeats : "";
  };

  const buildCell = (data, formation1, formation2, overridenValue = null, end = "%") => {
    const value = overridenValue != null ? overridenValue : getDetailCellValue(data);
    const matchCount = countMatches(data);

    return (
      <td
        className={"position-relative td-hover " + (matchCount > 0 && "filled")}
        key={formation1 || formation2}
        style={
          formation1 === formation2
            ? {
                backgroundColor: "rgba(255, 255, 255, 0.10)",
              }
            : {
                backgroundColor: `rgba(248, 98, 133, ${
                  value ? (!detailedView || selectedDisplay.includes("rate") ? value / 100 / 4 : value / 3 / 4) : 0
                })`,
              }
        }
      >
        <PopupFormationInfo
          trigger={
            <div>
              {overridenValue ? value + end : value}
              {value && !overridenValue && selectedDisplay.includes("rate") && end}
              <div
                className="position-absolute"
                style={{
                  right: "2px",
                  bottom: "-2px",
                  fontSize: "10px",
                  color: "rgba(255, 255, 255, 0.3)",
                }}
              >
                {matchCount}
              </div>
            </div>
          }
          onClose={() => console.log("closed")}
          data={data}
          engine={selectedEngine}
          formation1={formation1}
          formation2={formation2}
          maxOverallDifference={selectedDifference}
        />
      </td>
    );
  };

  useEffect(() => {
    fetchFormationMetaEngines();
  }, []);

  useEffect(() => {
    setFormationMetas(null);
    setDataMap(null);
    setTotalMatches(null);
    setGlobalDataMap(null);
    setFormations(null);
    if (selectedEngine) fetchFormationMetas();
    return () => {
      formationMetaRequestId.current += 1;
    };
  }, [selectedEngine, selectedDifference, selectedType]);

  return (
    <div id="PageFormationMeta" className="d-flex flex-column w-100 h-100">
      <nav className="MenuTop d-flex navbar">
        <h3 className="my-2 ps-5">
          <i className="bi bi-grid-3x3-gap-fill me-2"></i> Formation meta
        </h3>
      </nav>

      <div className="d-flex flex-fill flex-column flex-lg-row px-2 px-md-4 py-4 w-100" style={{ minHeight: 0 }}>
        <div className="d-flex flex-lg-basis-300 flex-column" style={{ minWidth: 300 }}>
          <BoxCard
            title={"Filters"}
            actions={
              <div className="d-flex h-100 align-items-start">
                <PopupInformationFormationMeta />
              </div>
            }
            content={
              engines ? (
                <div className="d-flex w-100 flex-column">
                  <div className="d-flex flex-row mb-1 flex-row justify-content-end">
                    <ButtonSelect
                      ariaLabel="Match type"
                      className="d-flex flex-row border me-2"
                      buttons={{ PRO: "Pro", ARENA: "Arena" }}
                      selected={selectedType}
                      onChange={setSelectedType}
                      selectedClassName="btn-info text-white"
                    />
                    <ButtonSelect
                      className="d-flex flex-row border"
                      buttons={{ false: "Compact", true: "Detailed" }}
                      selected={String(detailedView)}
                      onChange={(value) => setDetailedView(value === "true")}
                      selectedClassName="btn-info text-white"
                    />
                  </div>

                  <div className="d-flex flex-column">
                    <select
                      className="form-control w-100 text-white mb-1"
                      value={selectedEngine}
                      onChange={(v) => setSelectedEngine(v.target.value)}
                    >
                      {engines.map((p) => (
                        <option value={p.toString()} key={p.toString()}>
                          {"Engine v" + p}
                        </option>
                      ))}
                    </select>
                    <div className="position-relative">
                      {!isUserPremium(props.assistantUser) && (
                        <i
                          className="bi bi-lock-fill position-absolute"
                          style={{
                            right: "14px",
                            top: "45%",
                            transform: "translateY(-50%)",
                            zIndex: 1,
                          }}
                        />
                      )}
                      {isUserPremium(props.assistantUser) ? (
                        <select
                          className="form-control w-100 text-white mb-1 premium-background"
                          value={selectedDifference}
                          onChange={(v) => setSelectedDifference(Number(v.target.value))}
                        >
                          {[11, 22, 33].map((p) => (
                            <option value={p} key={p}>
                              {p + " OVR difference"}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <PopupPremiumInfo
                          trigger={
                            <select
                              className="form-control w-100 text-white mb-1 premium-background"
                              value={selectedDifference}
                              onChange={(v) => setSelectedDifference(Number(v.target.value))}
                            >
                              {[22].map((p) => (
                                <option value={p} key={p}>
                                  {p + " OVR difference"}
                                </option>
                              ))}
                            </select>
                          }
                        />
                      )}
                    </div>
                    {detailedView && (
                      <select
                        className="form-control w-100 text-white mb-1"
                        value={selectedDisplay}
                        onChange={(v) => setSelectedDisplay(v.target.value)}
                      >
                        {displays.map((p) => (
                          <option value={p} key={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="d-flex justify-content-end align-items-center mt-1">
                    <Popup
                      trigger={
                        <button
                          style={{ display: "None" }}
                          className={
                            "btn align-self-end " + (isUserPremium(props.assistantUser) ? "btn-info" : "btn-premium")
                          }
                        >
                          <i class="bi bi-sliders2"></i> Advanced filters
                        </button>
                      }
                      modal
                      closeOnDocumentClick={false}
                      onClose={() => {}}
                      onOpen={() => {}}
                      className={"fade-in popup-md"}
                    >
                      {(close) => (
                        <div>
                          <div className="d-flex flex-row mb-3">
                            <div className="flex-grow-1">
                              <h2 className="text-white">
                                <i class="bi bi-sliders2"></i> Advanced filters
                              </h2>
                            </div>
                            <div className="flex-grow-0">
                              <button className={"btn"} onClick={close}>
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          </div>

                          <div className="d-flex flex-wrap m-1 my-3 px-2 py-1 bg-black">
                            THIS SECTION IS NOT IMPLEMENTED YET
                          </div>

                          {!isUserPremium(props.assistantUser) && (
                            <div className="d-flex flex-wrap m-1 my-3 px-2 py-1 bg-black">
                              This section is disabled as you don't have Premium access.
                              <PopupPremiumInfo
                                trigger={
                                  <div className="text-premium cursor-pointer">Click here for more information</div>
                                }
                              />
                            </div>
                          )}

                          <div className="mb-3">
                            <div className="d-flex flex-row m-1">
                              <div className="flex-grow-1 align-self-center">Accepted OVRs:</div>
                              <input
                                className="form-control w-auto flex-grow-0 me-1"
                                disabled={!isUserPremium(props.assistantUser)}
                                value={minOverall}
                                placeholder={"Min OVR"}
                                onChange={(v) =>
                                  setMinOverall(v.target.value === "" ? undefined : parseInt(v.target.value, 10))
                                }
                                type="number"
                                min="1"
                                max="1100"
                                step="1"
                              />
                              <input
                                className="form-control w-auto flex-grow-0 me-1"
                                disabled={!isUserPremium(props.assistantUser)}
                                value={maxOverall}
                                placeholder={"Max OVR"}
                                onChange={(v) =>
                                  setMaxOverall(v.target.value === "" ? undefined : parseInt(v.target.value, 10))
                                }
                                type="number"
                                min="1"
                                max="1100"
                                step="1"
                              />
                            </div>

                            <div className="d-flex flex-row m-1">
                              <div className="flex-grow-1 align-self-center">Max OVR difference:</div>
                              <input
                                className="form-control w-auto flex-grow-0 me-1"
                                disabled={!isUserPremium(props.assistantUser)}
                                value={maxDiff}
                                onChange={(v) =>
                                  setMaxDiff(v.target.value === "" ? undefined : parseInt(v.target.value, 10))
                                }
                                type="number"
                                min="1"
                                max="99"
                                step="1"
                              />
                            </div>
                          </div>

                          <div className="d-flex flex-row justify-content-end mt-3">
                            <div>
                              <button
                                disabled={!isUserPremium(props.assistantUser)}
                                className="btn btn-info text-white"
                                onClick={() => {}}
                              >
                                Confirm
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </Popup>
                  </div>
                </div>
              ) : (
                <LoadingSquare height={"80px"} />
              )
            }
          />
        </div>

        <div
          className="height-1-lg d-flex flex-fill flex-grow-1"
          style={{ minWidth: 0, minHeight: 0, overflow: "hidden" }}
        >
          {selectedEngine && (
            <BoxCard
              className="formation-box d-flex flex-fill overflow-auto w-100 h-100"
              content={
                formationMetas && formations && dataMap ? (
                  <div className="" style={{ minHeight: 0, minWidth: 0 }}>
                    <table
                      className="formation-table"
                      cellPadding="5"
                      style={{
                        borderCollapse: "collapse",
                        width: "100%",
                      }}
                    >
                      <thead>
                        <tr>
                          <th className="sticky top-left" style={{ minWidth: "120px" }}>
                            {totalMatches && (
                              <>
                                <div>{totalMatches}</div>
                                <div>Matches</div>
                              </>
                            )}
                          </th>
                          {detailedView ? (
                            <>
                              {formations.map((formation2) => (
                                <th className="sticky top" key={formation2}>
                                  {shortenFormationName(formation2)}
                                </th>
                              ))}
                            </>
                          ) : (
                            <>
                              <th className="sticky top th-hover" onClick={() => handleSort("victories")}>
                                <div>Victory</div>
                                {sortConfig.key === "victories" ? (sortConfig.direction === "asc" ? "▼" : "▲") : ""}
                              </th>
                              <th className="sticky top th-hover" onClick={() => handleSort("draws")}>
                                <div>Draw</div>{" "}
                                {sortConfig.key === "draws" ? (sortConfig.direction === "asc" ? "▼" : "▲") : ""}
                              </th>
                              <th className="sticky top th-hover" onClick={() => handleSort("defeats")}>
                                <div>Defeat</div>
                                {sortConfig.key === "defeats" ? (sortConfig.direction === "asc" ? "▼" : "▲") : ""}
                              </th>
                              <th className="sticky top th-hover" onClick={() => handleSort("goalsScored")}>
                                <div>Goals scored</div>
                                {sortConfig.key === "goalsScored" ? (sortConfig.direction === "asc" ? "▼" : "▲") : ""}
                              </th>
                              <th className="sticky top th-hover" onClick={() => handleSort("goalsConceded")}>
                                <div>Goals conceded</div>
                                {sortConfig.key === "goalsConceded" ? (sortConfig.direction === "asc" ? "▼" : "▲") : ""}
                              </th>
                              <th className="sticky top th-hover" onClick={() => handleSort("difference")}>
                                <div>Goal difference</div>
                                {sortConfig.key === "difference" ? (sortConfig.direction === "asc" ? "▼" : "▲") : ""}
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(detailedView ? formations : sortedFormations).map((formation1) => (
                          <tr key={formation1}>
                            <th className="sticky left" style={{ minWidth: "120px" }}>
                              {shortenFormationName(formation1)}
                            </th>
                            {!detailedView && (
                              <>
                                {buildCell(
                                  globalDataMap[formation1],
                                  formation1,
                                  null,
                                  Math.round(
                                    (globalDataMap[formation1].victories /
                                      (globalDataMap[formation1].victories +
                                        globalDataMap[formation1].draws +
                                        globalDataMap[formation1].defeats)) *
                                      100,
                                  ),
                                )}
                                {buildCell(
                                  globalDataMap[formation1],
                                  formation1,
                                  null,
                                  Math.round(
                                    (globalDataMap[formation1].draws /
                                      (globalDataMap[formation1].victories +
                                        globalDataMap[formation1].draws +
                                        globalDataMap[formation1].defeats)) *
                                      100,
                                  ),
                                )}
                                {buildCell(
                                  globalDataMap[formation1],
                                  formation1,
                                  null,
                                  Math.round(
                                    (globalDataMap[formation1].defeats /
                                      (globalDataMap[formation1].victories +
                                        globalDataMap[formation1].draws +
                                        globalDataMap[formation1].defeats)) *
                                      100,
                                  ),
                                )}
                                {buildCell(
                                  globalDataMap[formation1],
                                  formation1,
                                  null,
                                  Math.round(
                                    (globalDataMap[formation1].goalsScored /
                                      (globalDataMap[formation1].victories +
                                        globalDataMap[formation1].draws +
                                        globalDataMap[formation1].defeats)) *
                                      100,
                                  ) / 100,
                                  "",
                                )}
                                {buildCell(
                                  globalDataMap[formation1],
                                  formation1,
                                  null,
                                  Math.round(
                                    (globalDataMap[formation1].goalsConceded /
                                      (globalDataMap[formation1].victories +
                                        globalDataMap[formation1].draws +
                                        globalDataMap[formation1].defeats)) *
                                      100,
                                  ) / 100,
                                  "",
                                )}
                                {buildCell(
                                  globalDataMap[formation1],
                                  formation1,
                                  null,
                                  Math.round(
                                    (Math.round(
                                      (globalDataMap[formation1].goalsScored /
                                        (globalDataMap[formation1].victories +
                                          globalDataMap[formation1].draws +
                                          globalDataMap[formation1].defeats)) *
                                        100,
                                    ) /
                                      100 -
                                      Math.round(
                                        (globalDataMap[formation1].goalsConceded /
                                          (globalDataMap[formation1].victories +
                                            globalDataMap[formation1].draws +
                                            globalDataMap[formation1].defeats)) *
                                          100,
                                      ) /
                                        100) *
                                      100,
                                  ) / 100,
                                  "",
                                )}
                              </>
                            )}
                            {detailedView &&
                              formations.map((formation2) => {
                                const key = `${formation1}||${formation2}`;
                                return buildCell(dataMap[key], formation1, formation2);
                              })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="w-100 h-100">
                    <LoadingSquare />
                  </div>
                )
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PageFormationMeta;
